// Reading a typed date, on the client.
//
// A copy of the parser in server/src/dates.ts, for the same reason as states.ts: two packages,
// two builds. The server is the authority -- it re-parses everything on save -- but the form
// needs the same answer locally to decide whether a stored value can be shown in the calendar
// or has to stay in a text box. A test in the server asserts the two agree.

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** Days in a month, so 31 February is rejected rather than rolled into March. */
function daysIn(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function build(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysIn(year, month)) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * A calendar date as `YYYY-MM-DD`, or null if the text is not one.
 *
 * Two-digit years are refused rather than guessed. Guessing the century of a date of birth is
 * how you end up with an applicant born in 1907, and the applicant is the only one who knows.
 */
export function toIsoDate(input: string): string | null {
  // Legacy values arrive wrapped: "(07/13/1989)".
  const text = input.trim().replace(/^\(+|\)+$/g, '').trim();
  if (!text) return null;

  // What the date control sends, and what we store.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) return build(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // Typed: 3/12/1989, 3-12-1989, 3.12.1989, 3 12 1989. Month first, as the form asks.
  const parts = /^(\d{1,2})[\/\-. ](\d{1,2})[\/\-. ](\d{4})$/.exec(text);
  if (parts) return build(Number(parts[3]), Number(parts[1]), Number(parts[2]));

  // Run together: 03241989.
  const packed = /^(\d{2})(\d{2})(\d{4})$/.exec(text);
  if (packed) return build(Number(packed[3]), Number(packed[1]), Number(packed[2]));

  // Written out: "January 22, 1990", "22 January 1990", "november 5, 1989".
  const monthFirst = /^([a-z]+)\W+(\d{1,2})\W+(\d{4})$/i.exec(text);
  const dayFirst = /^(\d{1,2})\W+([a-z]+)\W+(\d{4})$/i.exec(text);
  const named = monthFirst
    ? { month: monthFirst[1]!, day: monthFirst[2]!, year: monthFirst[3]! }
    : dayFirst
      ? { month: dayFirst[2]!, day: dayFirst[1]!, year: dayFirst[3]! }
      : null;
  if (named) {
    // Prefix match, so "Sept 5 1989" and "Jan 22, 1990" work as well as the full name.
    const month = MONTHS.findIndex((m) => m.startsWith(named.month.toLowerCase())) + 1;
    if (month > 0) return build(Number(named.year), month, Number(named.day));
  }

  return null;
}
